import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, Container, Typography } from "@mui/material";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";

import GradientIcon from "../components/GradientIcon";
import HaveQuestions from "../components/HaveQuestions";


import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';

import DAMTrainingCourseBG from "../assets/DAMTrainingCourse-bg.png";
import Hooshyar from "../assets/Hooshyar.png"
import halfCircles from "../assets/half-circles.png";

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "24px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};


const DamTrainingCorporatePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

const InfoRow = ({ icon: Icon, label, value, highlight, valueSx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25, // reduced from 2
      py: 1.5,
      px: 2,
      borderRadius: "12px",
      backgroundColor: highlight ? "#F5F5F5" : "transparent",
    }}
  >
    {/* Icon */}
    <Box sx={{ mt: "1px" }}>
      <GradientIcon icon={Icon} size={24} />
    </Box>

    {/* Text */}
    <Box sx={{
                display: "flex",
                flexDirection: {
                  xs: "column", // mobile
                  sm: "row",    // tablet & desktop
                },
                alignItems: {
                  xs: "flex-start",
                  sm: "baseline",
                },
                gap: {
                  xs: 0.5,
                  sm: 1.5,
                },                
              }}>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          lineHeight: { xs: 1.2, sm: 1.8 },
          mb: { xs: 0.4, sm: 0 },
        }}
      >
        {label}:
      </Typography>

      <Typography
        sx={{
          color: "#555",
          lineHeight: { xs: 1.4, sm: 1.8 },
          ...valueSx,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);
    const TakeawayItem = ({ icon: Icon, text }) => (

         <Box
            sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                        background: "linear-gradient(107.5deg, rgba(114, 79, 211, 0) 25.72%, rgba(252, 134, 175, 0.1) 88.01%)",
                  },
            }}
        >

          {/* Icon container */}
          <GradientIcon icon={Icon} size={24} bordered boxSize={40} borderRadius={10}
                  borderWidth={2}
                  angle="90"/>


          {/* Text */}
          <Typography sx={{ color: "#333", fontSize: "1rem" }}>
            {text}
          </Typography>
        </Box>
      );

  return (
    <Box sx={{ backgroundColor: "#F9F9F9" }}>
      <Helmet>
        <title>DAM Journey - DAM Training Corporate</title>
        <meta
          name="description"
          content="DAM Training Course, Master the principles of Digital Asset Management through a structured, product-agnostic training program designed for real-world implementation"
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, DAM Training Course, DAM Training Corporate"
        />
      </Helmet>
      <Box >
        {/* Hero Banner Section */}
        <Box
          sx={{
            width: "100%",
            height: "350px",
            background: "linear-gradient(0deg, #2A2177 0%, #0624BE 100%)",
            display: "flex",
            alignItems: "center",
            pl: "84px",
            pr: "219px",
            py: "74px",
            overflow: "hidden",
            position: "relative",
            "@media (max-width: 1200px)": {
              px: "24px",
              py: "78px",
            },
            "@media (max-width: 780px)": {
              flexDirection: "column",
              textAlign: "center",
              px: "16px",
              pt: "48px",
              pb: 2,
              height: "420px",
            },
          }}
        >
          {/* Left Text Content */}
          <Box
            sx={{
              flex: 2,
              zIndex: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.3,
                mb: 2,
                "@media (max-width: 1200px)": {
                  fontSize: "1.75rem",
                  fontWeight: "semiBold",
                },
                "@media (max-width: 780px)": {
                  fontSize: "1.5rem",
                  mb: 1,
                },
              }}
            >
              DAM Training Course

            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 1.7,
                maxWidth: "624px",
                "@media (max-width: 1200px)": {
                  fontSize: "18px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "16px",
                },
              }}
            >
              Master the principles of Digital Asset Management through a structured,
              product-agnostic training program designed for real-world implementation.
            </Typography>
          </Box>

          {/* Right DAM Training Course BG Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {/* Background Half Circles */}
            <Box
              component="img"
              src={halfCircles}
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "599px",
                height: "350px",
                objectFit: "contain",
                left: -40,
                "@media (max-width: 1200px)": {
                  left: 0,
                },
                "@media (max-width: 780px)": {
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "370px",
                  height: "auto",
                },
                zIndex: 0,
              }}
            />
            {/* Foreground DAM Training Course-bg */}
            <Box
              component="img"
              src={DAMTrainingCourseBG}
              sx={{
                position: "relative",
                zIndex: 1,
                width: "541px",
                height: "231px",
                "@media (max-width: 1200px)": {
                  width: "541px",
                  height: "231px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "18px",
                  width: "317px",
                  height: "135px",
                },
              }}
            />
          </Box>
        </Box>



        {/* Course Description Section */}

        <Box
          sx={{
            py: {xs: "30px",
                   sm: "48px",
                   lg: "48px"},

            px:   {xs: "16px",
                   sm: "24px",
                   lg: "84px"}
          }}
        >

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              textAlign: "center",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
              },
            }}
          >
            Course Description
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 780px)": {
                fontSize: "16px",
              },
            }}
          >
            <p>
              A Digital Asset Management (DAM) system provides a centralized platform for storing,
              organizing, managing, retrieving, and distributing digital files across all teams and
              departments within an organization. It plays a critical role in enabling fast content
              discoverability with high relevancy.
            </p>
            <p>
              DAM is the cornerstone of digital transformation initiatives and serves as a
              “single source of truth” for all digital assets. This training program is designed to provide a deep,
              product-agnostic understanding of DAM principles, enabling participants to apply their knowledge
              across any DAM platform.
            </p>
            <p>
             This course is neutral about any DAM software product.
             This means that the course material is not developed around a particular DAM product,
             so you are not locked into learning one software product. Essentially,
             you will gain a deep understanding of the principles of DAM so that you apply them
             to any DAM product that you may be using now or in the future.
            </p>
          </Typography>
        </Box>

          {/* Course Details & Course Takeaways Cards */}

          <Box
            sx={{
              display: "flex",
              alignItems: {xs: "stretch", md: "flex-start" },
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              justifyContent: "center",
              backgroundColor: "#ffffff",
              borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              py: {xs: "30px",
                   sm: "48px",
                   lg: "48px"},

              px:   {xs: "16px",
                   sm: "24px",
                   lg: "84px"}
            }}
          >
            {/* Course Details Card */}
            <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  p: 4,
                  boxShadow: "0px 4px 4px 0px #0000001F",
                  border: "1px solid #0000001A"
                }}
              >
                {/* Header */}
                <Typography
                  sx={{
                   fontSize: "2.5rem",
                   "@media (max-width: 780px)": {
                      fontSize: "2rem",
                      textAlign: "center",
                    },
                    fontWeight: 700,
                    color: "#2A2177",
                    mb: 1,
                  }}
                >
                  Course Details
                </Typography>

                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    "@media (max-width: 780px)": {
                      fontSize: "1.25rem",
                      textAlign: "center",
                    },
                    color: "#333",
                    mb: 3,
                  }}
                >
                  Principles of Digital Asset Management
                </Typography>

                   {/* Info Rows */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <InfoRow
                      icon={AccessTimeIcon}
                      label="Duration"
                      value="6 Weeks"
                    />

                    <InfoRow
                      icon={DesktopWindowsOutlinedIcon}
                      label="Format"
                      value="Online (Zoom + Interactive Sessions)"
                      highlight
                    />

                    <InfoRow
                      icon={EventOutlinedIcon}
                      label="Session Timing"
                      value="Thursday, 6:00 PM – 8:40 PM PST"
                    />

                    <InfoRow
                      icon={SchoolOutlinedIcon}
                      label="Instructor"
                      value="Hooshyar Naraghi"
                      highlight
                      valueSx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {/* Description */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mt: 3,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      component="img"
                      src={Hooshyar}

                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "12px",
                        objectFit: "cover",
                      }}
                    />

                      <Typography sx={{ color: "#555", mb: 1 ,fontSize: "0.875rem"}}>
                        This course provides a comprehensive understanding of Digital Asset
                        Management principles, focusing on organization-wide asset strategy,
                        discoverability, governance, and workflows.
                      </Typography>
                  </Box>

                  <Box sx={{display: "flex",gap: 0,mt: 0,alignItems: "flex-start",fontSize: "0.875rem"}}>
                    <Typography sx={{ color: "#555" }}>
                        <p>
                          Hooshyar Naraghi is the principal consultant at DAM Journey, LLC.
                          He is a subject matter expert in digital asset management (DAM),
                          content management systems (CMS), assets taxonomy and classification,
                          data migrations, and training with more than two decades of experience
                          across various business and engineering sectors. He operates a DAM practice
                          serving clients globally. Some of the companies he has consulted with are Apple Computers,
                          Moderna, Charter Communications, Culligan International, TD Ameritrade (now Charles Schwab),
                          ServiceNow, Brocade Communications (now Broadcom), Altair Engineering (now Siemens),
                          PLOS, AbbVie, and LINQ.
                        </p>
                        <p>
                          Hooshyar also has extensive experience delivering Digital Asset Management (DAM)
                          training at both corporate and academic levels.
                          The DAM curriculum he developed and teaches is considered one of the most
                          comprehensive programs in the DAM industry.
                        </p>
                    </Typography>
                  </Box>
                </Box>

            {/* Course Takeaways Card */}

            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "16px",
                p: 4,
                boxShadow: "0px 4px 4px 0px #0000001F",
                border: "1px solid #0000001A"

              }}
            >
              {/* Header */}
                <Typography
                  sx={{
                    fontSize: "2.5rem",
                    "@media (max-width: 780px)": {
                      fontSize: "2rem",
                    },
                    fontWeight: 700,
                    color: "#2A2177",
                    mb: 1,
                  }}
                >
                  Course Takeaways
                </Typography>

                <Typography

                  sx={{
                    fontSize: "1.5rem",
                    "@media (max-width: 780px)": {
                      fontSize: "1.25rem",
                    },
                    color: "#333",
                    mb: 3,
                    fontWeight: 500,
                  }}
                >
                  Practical skills and insights to apply DAM effectively
                </Typography>

            {/* Takeaways */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* items here */}

                  <TakeawayItem
                    icon={AutorenewOutlinedIcon}
                    text="Understand DAM fundamentals and asset lifecycle"
                  />
                  <TakeawayItem
                    icon={LocalOfferOutlinedIcon}
                    text="Learn metadata, taxonomy, tagging, and folder structure strategies"
                  />
                  <TakeawayItem
                    icon={AccountTreeOutlinedIcon}
                    text="Master asset workflows, share links, and collaboration"
                  />
                  <TakeawayItem
                    icon={HubOutlinedIcon}
                    text="Explore DAM architecture and integrations"
                  />
                  <TakeawayItem
                    icon={VerifiedUserOutlinedIcon}
                    text="Gain insights into governance and compliance"
                  />
                  <TakeawayItem
                    icon={AdminPanelSettingsOutlinedIcon}
                    text="Learn user management and access control"
                  />
                  <TakeawayItem
                    icon={ManageSearchOutlinedIcon}
                    text="Define asset search and discovery"
                  />
                  <TakeawayItem
                    icon={InsertChartOutlinedIcon}
                    text="Understand analytics and reporting"
                  />
                  <TakeawayItem
                    icon={TimelineOutlinedIcon}
                    text="Plan DAM implementation projects"
                  />
                  <TakeawayItem
                    icon={CompareArrowsOutlinedIcon}
                    text="Explore DAM vendor selection and RFP creation"
                  />
                  <TakeawayItem
                    icon={SchemaOutlinedIcon}
                    text="Master asset migration and asset mapping"
                  />
                  <TakeawayItem
                    icon={LightbulbOutlinedIcon}
                    text="Learn best practices from real-world case studies"
                  />

            </Box>
          </Box>
          </Box>



        {/* Course Registration Section */}
        <Box
          sx={{
            py: {xs: "30px",
                   sm: "48px",
                   md: "48px"},

            px:   {xs: "16px",
                   sm: "16px",
                   md: "48px"} ,
            backgroundColor: "#F9F9F9",
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",

          //  backgroundColor: "#ffffff",

          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              mb: 2,

            }}
          >
            Course Registration
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              "@media (max-width: 780px)": {
                fontSize: "16px",
              },
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
            }}
          >
            This DAM Training Course provides a platform for professionals to learn and experience
            the fundamentals of Digital Asset Management, covering its principles, practices, and
            techniques through an immersive experience.
          </Typography>
          {/* Cards Container */}
          <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",

                "@media (max-width: 780px)": {
                  gridTemplateColumns: "1fr"
                },
                gap: {
                  xs: 2,
                  sm: 2,
                  md: 3,
                },
                alignItems: "stretch",
                maxWidth: "1280px",
                mx: "auto",
              }}
            >

            {/* Who Should Attend */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={HowToRegOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Who Should Attend
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  This training program is for professionals who want to learn the fundamentals
                  of the DAM in an intensive course coverage. Whether you are a stakeholder or a
                  project team member planning to roll out or operate a DAM system,
                  you will benefit greatly from this course.
                </Typography>
              </Box>
            </Box>

            {/* Course Prerequisites */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,

                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={ChecklistOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Course Prerequisites
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  No technical prerequisite is required to sign up for this course.
                  The only  prerequisite is a deep and sincere desire to learn about the DAM.
                  Also, you should be comfortable working in a collaborative environment and be
                  willing to learn from and share knowledge with peers.
                </Typography>
              </Box>
            </Box>

            {/* Registration Process */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={AssignmentTurnedInOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Registration Process
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                    mb: 2,
                  }}
                >
                  Register for the DAM Training Course by submitting your application form. Click the link.
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<TouchAppOutlinedIcon />}
                  href="/dam-training-registration"
                  sx={{
                    background: "#2A2177",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.15)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #32267a, #4c3fc2)",
                    },
                  }}
                  >
                  Application Form
                </Button>

              </Box>
            </Box>

            {/* What You Will Get */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={CardGiftcardOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  What You Will Get
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  You master the core of digital asset management through a project-based
                  learning and in an immersive experience. You earn your certificate of
                  completion as proof of your skills in DAM.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

         {/* Have Questions Section */}
       <HaveQuestions />
      </Box>
    </Box>
  );
};

export default DamTrainingCorporatePage;
